import { html, type Html, View } from 'rune-ts';
import klass from './PromotionTooltip.module.scss';
import tippy from 'tippy.js';
import { makeInfoIcon } from '../../../../components/atoms/Icon';
import { font } from '@services/MarppleShop/shared/typography';

type Placement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'bottom-start'
  | 'left-start'
  | 'right-start'
  | 'top-end'
  | 'bottom-end'
  | 'left-end'
  | 'right-end';

type TooltipSizeType = 'small' | 'medium';

type TooltipSizes = {
  readonly [K in TooltipSizeType]: K;
};

export interface TooltipProps {
  isOpen?: boolean;
  id: string;
  content: string | Html | View;
  triggerClass?: string;
  contentClass?: string;
  size?: 'small' | 'medium';
  placement?: Placement;
}

export class PromotionTooltip extends View<TooltipProps> {
  private readonly trigger_id: string;

  static Sizes: TooltipSizes = {
    small: klass.small,
    medium: klass.medium,
  } as const;

  constructor(data: TooltipProps) {
    super(data);
    this.trigger_id = `${this.data.id}_trigger`;
  }

  protected override template(data: TooltipProps): Html {
    const { id, content, triggerClass = '', contentClass = '' } = data;

    return html`
      <div class="${klass.tooltip}" data-size="${this.size}">
        ${makeInfoIcon({
          id: this.trigger_id,
          klass: `${klass.trigger} ${triggerClass} ${PromotionTooltip.Sizes[this.size]}`,
        })}
        <script id="${id}" type="text/html">
          <div class="${klass.content} ${contentClass} ${font('14_bold')}">${content}</div>
        </script>
      </div>
    `;
  }

  override onMount() {
    let tooltipTimeout: ReturnType<typeof setTimeout> | null = null;
    const { id, placement = 'bottom-start' } = this.data;
    const content = document.getElementById(id);
    tippy(`#${this.trigger_id}`, {
      content: content!.innerHTML,
      trigger: 'click',
      allowHTML: true,
      appendTo: 'parent',
      placement,
      offset: [0, 10],
      popperOptions: {
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              padding: 16,
              boundariesElement: 'viewport',
            },
          },
        ],
      },
      hideOnClick: true,
      onShow(instance) {
        instance.reference.classList.add(klass.active);

        if (tooltipTimeout) {
          clearTimeout(tooltipTimeout);
        }

        tooltipTimeout = setTimeout(() => {
          instance.hide();
        }, 10000);
      },
      onHidden(instance) {
        instance.reference.classList.remove(klass.active);
      },
    });
  }

  get size() {
    return this.data.size || 'medium';
  }
}
